import { DateTime } from 'luxon';

export namespace Frequencies {
  export const DAILY = 'DAILY';
  export const WEEKLY = 'WEEKLY';
  export const MONTHLY = 'MONTHLY';
  export const YEARLY = 'YEARLY';

  export const ALL = [DAILY, WEEKLY, MONTHLY, YEARLY] as const;
}

export type Frequency = typeof Frequencies.ALL[number];

export namespace DaysOfWeek {
  export const SUNDAY = 'SU';
  export const MONDAY = 'MO';
  export const TUESDAY = 'TU';
  export const WEDNESDAY = 'WE';
  export const THURSDAY = 'TH';
  export const FRIDAY = 'FR';
  export const SATURDAY = 'SA';

  export const ALL = [
    SUNDAY,
    MONDAY,
    TUESDAY,
    WEDNESDAY,
    THURSDAY,
    FRIDAY,
    SATURDAY,
  ];
  export const WEEKDAYS = [MONDAY, TUESDAY, WEDNESDAY, THURSDAY, FRIDAY];
}

export type DayOfWeek = typeof DaysOfWeek.ALL[number];

export class RecurrenceRule {
  public readonly frequency: Frequency;

  public readonly byDay: DayOfWeek[];

  public readonly count: number;

  public readonly until: DateTime;

  constructor({
    frequency,
    byDay,
    count,
    until,
  }: {
    frequency: Frequency;
    byDay?: DayOfWeek[];
    count?: number;
    until?: DateTime;
  }) {
    if (!frequency) {
      throw new Error('frequency is required');
    }
    if (count && until) {
      throw new Error('cannot provide both count and until');
    }
    this.frequency = frequency;
    this.byDay = byDay;
    this.count = count;
    this.until = until;
  }

  public static parse(s: string) {
    let frequency: Frequency = null;
    let byDay: DayOfWeek[] = null;
    let count: number;
    let until: DateTime = null;
    s.split(';').forEach((part) => {
      const [name, value] = part.split('=', 2);
      switch (name) {
        case 'FREQ':
          frequency = Frequencies.ALL.find((f) => f === value);
          if (!frequency) {
            throw new Error(`unrecognized frequency: ${value}`);
          }
          break;
        case 'BYDAY':
          byDay = value.split(',').map((byDayPart) => {
            const day = DaysOfWeek.ALL.find((d) => d === byDayPart);
            if (!day) {
              throw new Error(`unrecognized weekday: ${byDayPart}`);
            }
            return day;
          });
          break;
        case 'COUNT':
          count = parseInt(value, 10);
          break;
        case 'UNTIL':
          until = DateTime.fromFormat(value, 'yyyy-MM-dd');
          if (!until.isValid) {
            throw new Error(`invalid UNTIL date: ${until.invalidExplanation}`);
          }
          break;
        default:
          throw new Error(`unrecognized recurrence rule part: ${part}`);
      }
    });
    return new RecurrenceRule({ frequency, byDay, count, until });
  }

  public replace(replace: {
    frequency?: Frequency;
    until?: DateTime;
  }): RecurrenceRule {
    return new RecurrenceRule({
      ...this,
      ...replace,
    });
  }

  public toString(): string {
    let result: string = `FREQ=${this.frequency}`;
    if (this.byDay) {
      result += `;BYDAY=${this.byDay.join(',')}`;
    }
    if (this.count) {
      result += `;COUNT=${this.count}`;
    }
    if (this.until) {
      result += `;UNTIL=${this.until.toISODate()}`;
    }
    return result;
  }

  public equals(other: RecurrenceRule) {
    return this.toString() === other.toString();
  }
}
